import React, { useState, useEffect } from 'react';
import speech from '../core/SpeechService';
import eventBus from '../core/EventBus';

function Header({ speechEnabled, onToggleSpeech, darkMode, onToggleDark, isAdmin, onAdminLogin, onAdminLogout }) {
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [activeVoice, setActiveVoice] = useState(speech.xaiVoice || 'ara');

  useEffect(() => {
    const unsub = eventBus.on('stage:click', () => setShowVoiceMenu(false));
    return () => unsub();
  }, []);

  const selectVoice = (id) => {
    speech.setXaiVoice(id);
    setActiveVoice(id);
    setShowVoiceMenu(false);
    speech.speak('Hallo! Ich bin Lenny, dein Designer-Assistent.');
  };

  return (
    <header className="h-12 bg-primary-500 flex items-center px-4 shrink-0 relative">
      <div className="flex items-center gap-2">
        <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M9 3v18M3 9h18" />
        </svg>
        <h1 className="text-white font-semibold text-lg tracking-tight">Selenny® Designer</h1>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <span className="text-white/70 text-xs hidden sm:inline">Der einzige Designer mit echten 3D-Schriften</span>
        {isAdmin ? (
          <button
            onClick={onAdminLogout}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 text-white hover:bg-white/25 transition-all"
            data-tooltip="Admin abmelden"
            data-tooltip-pos="bottom"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Admin
          </button>
        ) : onAdminLogin ? (
          <button
            onClick={onAdminLogin}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all"
            data-tooltip="Als Admin anmelden"
            data-tooltip-pos="bottom"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
            Anmelden
          </button>
        ) : null}
        {onToggleDark && (
          <button
            onClick={onToggleDark}
            className="p-1.5 rounded-lg transition-all text-white/60 hover:text-white hover:bg-white/10"
            data-tooltip={darkMode ? 'Light Mode' : 'Dark Mode'}
            data-tooltip-pos="bottom"
          >
            {darkMode ? (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
              </svg>
            )}
          </button>
        )}
        {onToggleSpeech && (
          <div className="relative">
            <button
              onClick={onToggleSpeech}
              onContextMenu={(e) => { e.preventDefault(); setShowVoiceMenu(!showVoiceMenu); }}
              className={`p-1.5 rounded-lg transition-all ${speechEnabled ? 'bg-white/20 text-white' : 'text-white/40 hover:text-white/70'}`}
              data-tooltip={speechEnabled ? 'Sprache an (Rechtsklick: Stimme waehlen)' : 'Sprache aus'}
              data-tooltip-pos="bottom"
            >
              {speechEnabled ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <path d="M15.54 8.46a5 5 0 010 7.07"/>
                  <path d="M19.07 4.93a10 10 0 010 14.14"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <line x1="23" y1="9" x2="17" y2="15"/>
                  <line x1="17" y1="9" x2="23" y2="15"/>
                </svg>
              )}
            </button>

            {/* Voice selection dropdown */}
            {showVoiceMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 py-2 z-[200] animate-slideIn">
                <p className="px-3 py-1 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Lennys Stimme</p>
                {speech.getXaiVoices().map(v => (
                  <button
                    key={v.id}
                    onClick={() => selectVoice(v.id)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                      activeVoice === v.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {activeVoice === v.id && (
                      <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                    )}
                    <span className={activeVoice !== v.id ? 'ml-5.5' : ''}>{v.name}</span>
                    <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500">{v.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
